import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { throwIfNotOk } from "@/lib/api-error";
import { MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";
import type { SearchUser } from "@/types";

export interface UserSearchPickerProps {
  companyId: string;
  onAdded: () => void;
  onCancel: () => void;
}

export function UserSearchPicker({
  companyId,
  onAdded,
  onCancel,
}: UserSearchPickerProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const searchTimeoutRef = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (searchTimeoutRef[0]) clearTimeout(searchTimeoutRef[0]);
    searchTimeoutRef[0] = setTimeout(() => setDebouncedQuery(value), 300);
  };

  const { data: results = [], isFetching: searching } = useQuery({
    queryKey: ["admin", "users", "search", debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length < 2) return [];
      const res = await fetch(
        `/api/users/search?q=${encodeURIComponent(debouncedQuery)}`
      );
      await throwIfNotOk(res, "Greška pri učitavanju podataka");
      return res.json() as Promise<SearchUser[]>;
    },
    enabled: debouncedQuery.length >= 2,
    placeholderData: (prev) => prev,
    staleTime: 1000 * 30,
  });

  const addMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`/api/companies/${companyId}/supervisors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "Greška pri dodavanju");
      }
      return res.json();
    },
    onSuccess: onAdded,
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Pretražite po imenu ili emailu..."
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          className="pl-10"
          autoFocus
        />
      </div>

      {/* Results */}
      {debouncedQuery.length >= 2 && (
        <div className="max-h-48 overflow-y-auto rounded-lg border">
          {searching ? (
            <div className="flex items-center justify-center py-6">
              <CircleNotch className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : results.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nema rezultata za "{debouncedQuery}"
            </p>
          ) : (
            results.map((user) => (
              <button
                key={user.id}
                onClick={() => addMutation.mutate(user.id)}
                disabled={addMutation.isPending}
                className="flex w-full items-center gap-3 border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted/40 disabled:opacity-50"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {user.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {user.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </p>
                </div>
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {user.role}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {addMutation.error && (
        <p className="text-xs text-rose-600">
          {addMutation.error.message}
        </p>
      )}

      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Otkaži
        </Button>
      </div>
    </div>
  );
}
